import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Preference} from '@interfaces/preference';
import {ResponseDTO} from '@interfaces/general/response';
import {EnvironmentService} from '@services/environment.service';

export interface SocialLinks {
  instagram: string | null;
  facebook: string | null;
  whatsapp: string | null;
}

/**
 * Preferencias que el comercio configura desde el administrador, para lo que
 * tiene que poder cambiar sin rehacer el build de la tienda.
 */
@Injectable({providedIn: 'root'})
export class PreferenceService {

  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);
  private url = this.environmentService.getUrl();

  /**
   * Las tres redes comparten prefijo porque el filtro `value` del backend hace
   * coincidencia parcial: una sola consulta trae las tres. Ademas evita el tope
   * de la respuesta, que devuelve 10 filas por pagina y no acepta que se le
   * pida un tamaño mayor: pedir todo `key=config` dejaria filas afuera.
   */
  private readonly socialPrefix = 'redessociales';

  #social = signal<SocialLinks>({instagram: null, facebook: null, whatsapp: null});
  public social = this.#social.asReadonly();

  constructor() {
    this.loadSocial();
  }

  private loadSocial(): void {
    // La URL va armada a mano y no con HttpParams porque el backend habilita el
    // acceso anonimo comparando el comienzo del path: tiene que empezar
    // exactamente con "/preferenceService/listSearch/0?key=config". Si el orden
    // de los parametros cambia, la consulta pasa a pedir token y responde 401.
    const url = `${this.url}preferenceService/listSearch/0?key=config&value=${this.socialPrefix}`;

    this.http.get<ResponseDTO<Preference>>(url).subscribe({
      next: (res) => this.#social.set({
        instagram: this.pick(res.rows, 'instagram'),
        facebook: this.pick(res.rows, 'facebook'),
        whatsapp: this.pickWhatsapp(res.rows),
      }),
      // Sin redes configuradas la tienda funciona igual: los iconos no se
      // muestran y no hace falta avisar nada.
      error: () => this.#social.set({instagram: null, facebook: null, whatsapp: null}),
    });
  }

  /**
   * El numero se copia y pega desde la agenda del telefono, asi que llega con
   * el +, con espacios o guiones, y hasta con marcas de direccion de texto
   * invisibles (U+202A y U+202C). El link de WhatsApp solo acepta digitos: con
   * cualquiera de esas sobras no abre la conversacion.
   */
  private pickWhatsapp(rows: Preference[]): string | null {
    const raw = this.pick(rows, 'whatsapp');
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    return digits ? digits : null;
  }

  private pick(rows: Preference[], network: string): string | null {
    const row = rows.find(r => r.value === `${this.socialPrefix}-${network}`);
    const value = row?.value2?.trim();
    return value ? value : null;
  }
}
